import type { HTMLAttributes, ReactNode, Ref } from 'react'

/**
 * Badge color variants (shared across Badge and StatusBadge)
 */
export const BadgeVariant = {
  Neutral: 'neutral',
  Processing: 'processing',
  Success: 'success',
  Warning: 'warning',
  Danger: 'danger',
  Special: 'special',
} as const

export type BadgeVariant = (typeof BadgeVariant)[keyof typeof BadgeVariant]

/**
 * Standard Badge props
 */
export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  ref?: Ref<HTMLSpanElement>
  /** Content rendered before children (icons, indicators, etc.) */
  leftSection?: ReactNode
  /** Content rendered after children (icons, badges, etc.) */
  rightSection?: ReactNode
  /** Badge color variant */
  variant?: BadgeVariant
  /** Ellipsize the label when the badge is width-constrained */
  truncate?: boolean
}

/**
 * StatusBadge props - lighter badges for entity statuses.
 * Accepts a variant, an icon, and a label (children); without a label it
 * renders icon-only, named by `aria-label`.
 * Domain-specific status-to-variant mapping belongs at the app level.
 */
export type StatusBadgeProps = Omit<
  HTMLAttributes<HTMLSpanElement>,
  'children'
> & {
  ref?: Ref<HTMLSpanElement>
  /** Badge color variant */
  variant?: BadgeVariant
} & (
    | {
        /** Icon rendered before the label */
        icon?: ReactNode
        /** Badge label */
        children: ReactNode
      }
    | {
        /** The badge's only content, shown in a square */
        icon: ReactNode
        children?: never
        /** Names the badge, since it has no visible label */
        'aria-label': string
      }
  )
